/**
 * Silent in-app recovery for AIP localStorage answer data.
 *
 * Reps' answers entered before the saveAnswer Supabase write was wired up
 * (see 2026-05-11 post-mortem) are stranded in localStorage under
 * `aip:helix:{accountId}` keys. This module ports the
 * `/home/user/workspace/aip-recovery/replay.js` console script into the app
 * so it runs automatically on the user's next login after deploy.
 *
 * Invariants:
 *   - Never surfaces to the rep (no UI, no toasts, no thrown errors).
 *   - Never deletes localStorage; data stays as backup.
 *   - Never blocks render — runs fire-and-forget from a useEffect.
 *   - Runs at most once per browser via the `aip:recovery:done:v1` flag.
 *   - Idempotent: upserts on (account_id, question_key), safe to re-run.
 */
import { supabase } from "@/lib/session";

const LS_ANSWER_RE = /^aip:helix:(\d+)$/;
const RECOVERY_FLAG_KEY = "aip:recovery:done:v1";
const BATCH_SIZE = 50;

type AnswerEntry = {
  selectedOption?: string | null;
  notes?: string;
  updatedAt?: string;
  updatedBy?: string;
};

type AnswerPayload = {
  account_id: number;
  question_key: string;
  selected_option: string | null;
  answer_notes: string;
  updated_at: string;
};

type FailureRecord = {
  account_id?: number;
  question_key?: string;
  reason: string;
};

export async function runSilentRecovery(userId: string): Promise<void> {
  try {
    if (!userId) return;

    // Per-browser guard: only ever run once per device.
    try {
      if (localStorage.getItem(RECOVERY_FLAG_KEY)) return;
    } catch {
      return;
    }

    // ── Scan localStorage for `aip:helix:{digits}` keys ─────────────────────
    const payloads: AnswerPayload[] = [];
    const scannedAccounts: number[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const match = key.match(LS_ANSWER_RE);
      if (!match) continue;

      const accountId = parseInt(match[1], 10);
      if (Number.isNaN(accountId)) continue;
      scannedAccounts.push(accountId);

      let store: Record<string, AnswerEntry>;
      try {
        const raw = localStorage.getItem(key);
        store = raw ? JSON.parse(raw) : {};
      } catch {
        continue;
      }
      if (!store || typeof store !== "object") continue;

      for (const [questionKey, entry] of Object.entries(store)) {
        if (!entry || typeof entry !== "object") continue;
        if (entry.selectedOption === undefined) continue;
        payloads.push({
          account_id: accountId,
          question_key: questionKey,
          selected_option: entry.selectedOption,
          answer_notes: entry.notes ?? "",
          updated_at: entry.updatedAt || new Date().toISOString(),
        });
      }
    }

    const scanned = payloads.length;

    // Nothing to recover — mark done and bail.
    if (scanned === 0) {
      await writeRecoveryLog(userId, 0, 0, 0, null, "no localStorage answers found");
      markDone();
      return;
    }

    // ── Batch upsert via the supabase-js client (reuses the rep's session). ──
    const sb = supabase();
    let recovered = 0;
    let failed = 0;
    const failures: FailureRecord[] = [];

    for (let offset = 0; offset < payloads.length; offset += BATCH_SIZE) {
      const batch = payloads.slice(offset, offset + BATCH_SIZE);
      const { error } = await sb
        .from("account_answers")
        .upsert(batch, { onConflict: "account_id,question_key" });

      if (!error) {
        recovered += batch.length;
        continue;
      }

      // Batch failed — retry each row to isolate failures.
      for (const row of batch) {
        const { error: rowErr } = await sb
          .from("account_answers")
          .upsert([row], { onConflict: "account_id,question_key" });
        if (!rowErr) {
          recovered += 1;
        } else {
          failed += 1;
          failures.push({
            account_id: row.account_id,
            question_key: row.question_key,
            reason: rowErr.message || "unknown",
          });
        }
      }
    }

    await writeRecoveryLog(
      userId,
      scanned,
      recovered,
      failed,
      failures.length ? failures : null,
      `accounts scanned: ${scannedAccounts.join(",")}`,
    );

    // Mark done only if every upsert landed. Partial failure leaves the flag
    // unset so the next login retries the stragglers.
    if (failed === 0) markDone();
  } catch {
    // Never throw to caller.
  }
}

function markDone(): void {
  try {
    localStorage.setItem(RECOVERY_FLAG_KEY, new Date().toISOString());
  } catch {
    // ignore
  }
}

async function writeRecoveryLog(
  userId: string,
  scanned: number,
  recovered: number,
  failed: number,
  failureLog: FailureRecord[] | null,
  notes: string,
): Promise<void> {
  try {
    await supabase()
      .from("recovery_log")
      .insert({
        user_id: userId,
        scanned,
        recovered,
        failed,
        failure_log: failureLog,
        notes,
      });
  } catch {
    // recovery_log table may not exist yet — silently swallow.
  }
}
